// @ts-strict-ignore
// keep this file isomorphic
import moment from 'moment';

export interface CalendarMonth {
  year: number;
  month: number; // zero-based numbering
}

export interface CalendarDay {
  year: number;
  month: number; // zero-based numbering
  isoDayOfMonth: number; // 1-based index, matches ISO format
}

export interface DayLayout {
  columnStart: number; // 1-based index, matches CSS
  rowStart: number; // 1-based index, matches CSS
  calendarDay: CalendarDay;
  disabled: boolean;
}

interface MonthLayout {
  rowStart: number; // 1-based index, matches CSS
  label: string;
  calendarMonth: CalendarMonth;
}

export interface MonthGridLayout {
  days: Map<string, DayLayout>; // key from getCalendarDayKey()
  months: Map<string, MonthLayout>; // key from getCalendarMonthKey()
  rowHeight: number; // total number of grid rows

  startDayKey: string; // key from getCalendarDayKey()
  endDayKey: string; // key from getCalendarDayKey()

  topCanaryCalendarMonth: CalendarMonth;
  topCanaryRowStart: number; // 1-based index, matches CSS
  bottomCanaryCalendarMonth: CalendarMonth;
  bottomCanaryRowStart: number; // 1-based index, matches CSS
}

export enum SelectionSegmentType {
  Fill,
  Highlight,
  Offset,
}

export interface SelectionSegmentLayout {
  segmentType: SelectionSegmentType;
  verticalMarginPx: number;
  columnStart: number; // 1-based index, matches CSS
  columnSpan: number;
  rowStart: number; // 1-based index, matches CSS
  startRounded: boolean;
  endRounded: boolean;
  fadeIn: boolean;
  fadeOut: boolean;
  calendarDays: CalendarDay[];
}

export interface SelectionLayout {
  segments: SelectionSegmentLayout[];

  // will contain lots of duplicates but offers efficient per-day lookup for rendering
  activeSegmentForDays: Map<string, SelectionSegmentLayout>; // key from getCalendarDayKey()
}

// selection is primarily described by ISO 8601 strings instead of DateRange objects to keep its API independent and flexible,
// ScrollingCalendar is a building block component that shouldn't be coupled to application details (a higher level widget will handle DateRange for application usage)
export interface ScrollingCalendarDateSelection {
  start: string; // ISO 8601 date string, inclusive in range
  end: string; // ISO 8601 date string, inclusive in range
  highlightStart: boolean;
  highlightEnd: boolean;

  // date must be before "start" or after "end"
  offsetFrom?: string; // ISO 8601 date string, inclusive in offset range
}

export function getCalendarRangeKey(calendarRange: [CalendarMonth, CalendarMonth]): string {
  const [start, end] = calendarRange;
  return `${start.year}-${start.month}:${end.year}-${end.month}`;
}

export function getCalendarMonthKey(calendarMonth: CalendarMonth): string {
  return `${calendarMonth.year}-${calendarMonth.month}`;
}

export function getCalendarDayKey(calendarDay: CalendarDay): string {
  return `${calendarDay.year}-${calendarDay.month}-${calendarDay.isoDayOfMonth}`;
}

export function generateVisibleCalendarRange(
  focalMonth: CalendarMonth,
  monthPadding: number,
  disabledStartDate: null | moment.Moment /* resulting range is clamped to end on the same month as the disabled start date */,
  disabledUntilDate: null | moment.Moment /* resulting range is clamped to start on the same month as the disabled until date */,
): [CalendarMonth, CalendarMonth] {
  let start: moment.Moment = moment(focalMonth).add(-monthPadding, `month`);
  let end: moment.Moment = moment(focalMonth).add(monthPadding, `month`);

  // clamp visible calendar range to disabledUntilDate
  if (disabledUntilDate?.isAfter(start, `month`)) {
    start = disabledUntilDate;
    end = disabledUntilDate.clone().add(monthPadding * 2, `month`);
  }
  // but prefer clamping the range to disabledStartDate if the clamped dates overlap
  if (disabledStartDate?.isBefore(end, `month`)) {
    end = disabledStartDate;
    start = disabledStartDate.clone().add(monthPadding * -2, `month`);
  }
  return [
    {year: start.year(), month: start.month()},
    {year: end.year(), month: end.month()},
  ];
}

// calculating and applying explicit grid positions for every element instead of using "grid-auto-rows" lets us overlap grid cells
// and control DOM tree structure without affecting layout (controlling DOM tree structure lets us control the natural DOM stacking order without z-index madness, and can help with render optimization)
export function generateMonthGridLayout(
  calendarRange: [CalendarMonth, CalendarMonth] /* start & end inclusive */,
  weekdayStartOfWeek: number /* non-ISO weekday that starts on 0 = Sunday */,
  disabledStartDate: null | moment.Moment /* date from which all subsequent dates are disabled (excluding the start date itself) */,
  disabledUntilDate: null | moment.Moment /* date from which all prior dates are disabled (excluding the start date itself) */,
): MonthGridLayout {
  const [start, end] = calendarRange;

  const months = new Map<string, MonthLayout>();
  const days = new Map<string, DayLayout>();

  let gridRow = 1; // 1-based index, matches CSS
  const monthDate: moment.Moment = moment(start);
  while (monthDate.isSameOrBefore(moment(end), `day`)) {
    const calendarMonth = {year: monthDate.year(), month: monthDate.month()};

    // month label layout
    months.set(getCalendarMonthKey(calendarMonth), {
      rowStart: gridRow,
      label: `${moment.months(monthDate.month())} ${monthDate.year()}`,
      calendarMonth,
    });
    gridRow += 1;

    // day cell layout
    const daysInMonth = monthDate.daysInMonth();
    let isoDayOfMonth = 1;
    let dayOfWeek: number = moment({...calendarMonth, day: isoDayOfMonth}).weekday(); // non-ISO weekday that starts on 0 = Sunday
    let weekOfMonth = 0;
    while (isoDayOfMonth <= daysInMonth) {
      const calendarDay = {...calendarMonth, isoDayOfMonth};
      const disabled = (() => {
        const date = moment({...calendarMonth, day: isoDayOfMonth});
        return !!(disabledStartDate?.isBefore(date, `day`) || disabledUntilDate?.isAfter(date, `day`));
      })();
      days.set(getCalendarDayKey(calendarDay), {
        rowStart: gridRow + weekOfMonth,
        columnStart: ((dayOfWeek - weekdayStartOfWeek + 7) % 7) + 1,
        calendarDay,
        disabled,
      });

      isoDayOfMonth++;
      dayOfWeek = (dayOfWeek + 1) % 7;
      if (dayOfWeek === weekdayStartOfWeek && isoDayOfMonth <= daysInMonth) {
        weekOfMonth++;
      }
    }
    gridRow += weekOfMonth + 1;

    monthDate.add(1, `month`);
  }

  const secondMonthDate = moment(start).add(1, `month`);
  const secondLastMonthDate = moment(end).add(-1, `month`);

  const topCanaryCalendarMonth: CalendarMonth = {year: secondMonthDate.year(), month: secondMonthDate.month()};
  const bottomCanaryCalendarMonth: CalendarMonth = {
    year: secondLastMonthDate.year(),
    month: secondLastMonthDate.month(),
  };
  const topCanaryRowStart = months.get(getCalendarMonthKey(topCanaryCalendarMonth)).rowStart;
  const bottomCanaryRowStart = days.get(
    getCalendarDayKey({...bottomCanaryCalendarMonth, isoDayOfMonth: moment(bottomCanaryCalendarMonth).daysInMonth()}),
  ).rowStart;

  return {
    days,
    months,
    rowHeight: gridRow - 1,
    startDayKey: getCalendarDayKey({...start, isoDayOfMonth: 1}),
    endDayKey: getCalendarDayKey({...end, isoDayOfMonth: moment(end).daysInMonth()}),
    topCanaryCalendarMonth,
    bottomCanaryCalendarMonth,
    topCanaryRowStart,
    bottomCanaryRowStart,
  };
}

export function getDayLayoutFromDate(date: moment.Moment, monthGridLayout: MonthGridLayout): null | DayLayout {
  const dateKey = getCalendarDayKey({
    year: date.year(),
    month: date.month(),
    isoDayOfMonth: date.date(),
  });
  return monthGridLayout.days.get(dateKey) || null;
}

export function getScrollYFocusedOnDate(
  focalDate: moment.Moment,
  monthGridLayout: MonthGridLayout,
  scrollViewportHeight: number,
  rowHeightPx: number,
  headerOffsetPx: number,
  preferNearTop: boolean, // preference for the focalDate to be positioned near the top of the viewport (or near the bottom if false)
  disabledStartDate: null | moment.Moment,
  disabledUntilDate: null | moment.Moment,
): number {
  const focalDay = getDayLayoutFromDate(focalDate, monthGridLayout);
  if (!focalDay) {
    throw new Error(`focalDate must be within monthGridLayout`);
  }

  const focalDayY = focalDay.rowStart * rowHeightPx;
  const spaceFromEdge = rowHeightPx * 1.5; // 1.5 rows
  const yFromTop = focalDayY - rowHeightPx - spaceFromEdge;
  const yFromBottom = focalDayY + spaceFromEdge - scrollViewportHeight + headerOffsetPx;

  if (preferNearTop) {
    // if the focal date prefers to be positioned near the top of the viewport
    const disabledDay = disabledStartDate && getDayLayoutFromDate(disabledStartDate, monthGridLayout);
    if (disabledDay) {
      const disabledYFromBottom =
        disabledDay.rowStart * rowHeightPx + spaceFromEdge - scrollViewportHeight + headerOffsetPx;
      if (disabledYFromBottom < yFromTop) {
        // and if positioning near the top would show too many disabled dates
        // then position near the bottom instead
        return yFromBottom;
      }
    }
    // otherwise respect the preferred position near the top
    return yFromTop;
  } else {
    // if the focal date prefers to be positioned near the bottom of the viewport
    const disabledDay = disabledUntilDate && getDayLayoutFromDate(disabledUntilDate, monthGridLayout);
    if (disabledDay) {
      const disabledYFromTop = disabledDay.rowStart * rowHeightPx - rowHeightPx - spaceFromEdge;
      if (disabledYFromTop > yFromBottom) {
        // and if positioning near the bottom would show too many disabled dates
        // then position near the top instead
        return yFromTop;
      }
    }
    // otherwise respect the preferred position near the bottom
    return yFromBottom;
  }
}

export function getScrollYFocusedOnDateSelection(
  focalSelection: ScrollingCalendarDateSelection,
  monthGridLayout: MonthGridLayout,
  scrollViewportHeight: number,
  rowHeightPx: number,
  headerOffsetPx: number,
  disabledStartDate: null | moment.Moment,
  disabledUntilDate: null | moment.Moment,
  preferFocusStart?: boolean, // preference to focus on start date (if true) or end date (if false) when both dates cannot be focused at once
): number {
  const startDate = moment(focalSelection.start, moment.ISO_8601);
  const endDate = moment(focalSelection.end, moment.ISO_8601);

  const startDay = getDayLayoutFromDate(startDate, monthGridLayout);
  const endDay = getDayLayoutFromDate(endDate, monthGridLayout);

  const startY =
    startDay &&
    getScrollYFocusedOnDate(
      startDate,
      monthGridLayout,
      scrollViewportHeight,
      rowHeightPx,
      headerOffsetPx,
      true /* preferNearTop */,
      disabledStartDate,
      disabledUntilDate,
    );
  const endY =
    endDay &&
    getScrollYFocusedOnDate(
      endDate,
      monthGridLayout,
      scrollViewportHeight,
      rowHeightPx,
      headerOffsetPx,
      false /* preferNearTop */,
      disabledStartDate,
      disabledUntilDate,
    );

  if (startDay && endDay) {
    const dayYDiff = Math.abs(endDay.rowStart * rowHeightPx - startDay.rowStart * rowHeightPx);
    const isTallerThanViewport = dayYDiff > scrollViewportHeight - headerOffsetPx - rowHeightPx;
    if (isTallerThanViewport) {
      // pick either start or end based on preference
      if (preferFocusStart !== undefined) {
        return preferFocusStart ? startY : endY;
      }

      // pick either start or end based on its highlightStart/End
      const isHighlightStartOnly = focalSelection.highlightStart && !focalSelection.highlightEnd;
      return isHighlightStartOnly ? startY : endY;
    }

    // otherwise find a Y-value between startY and endY
    return Math.min(startY, endY) + Math.abs(endY - startY) / 2;
  } else if (startDay) {
    return startY;
  } else if (endDay) {
    return endY;
  }

  // fallback scroll to top or bottom
  if (preferFocusStart) {
    return 0;
  } else {
    const bottomDay = monthGridLayout.days.get(monthGridLayout.endDayKey);
    return bottomDay.rowStart * rowHeightPx - scrollViewportHeight + headerOffsetPx;
  }
}

// this is primarily an internal method, avoid exporting it or testing it directly
// TODO opslead
function generateSegmentsForSegmentType(
  monthGridLayout: MonthGridLayout,
  timespanStart: moment.Moment,
  timespanEnd: moment.Moment,
  rangeStart: null | moment.Moment, // rangeStart/End is purely for styling purposes, a segment at the start/end of the range has unique styling
  rangeEnd: null | moment.Moment,
  segmentType: SelectionSegmentType,
): SelectionSegmentLayout[] {
  const rangeStartKey = getCalendarDayKey({
    year: rangeStart?.year(),
    month: rangeStart?.month(),
    isoDayOfMonth: rangeStart?.date(),
  });
  const rangeEndKey = getCalendarDayKey({
    year: rangeEnd?.year(),
    month: rangeEnd?.month(),
    isoDayOfMonth: rangeEnd?.date(),
  });
  const rangeStartRowStart = monthGridLayout.days.get(rangeStartKey)?.rowStart;
  const rangeEndRowStart = monthGridLayout.days.get(rangeEndKey)?.rowStart;

  const minColumnForRows = new Map<number, number>(); // key is rowStart, value is minimum columnStart for that row
  const maxColumnForRows = new Map<number, number>(); // key is rowStart, value is maximum columnStart for that row
  const rowsCrossingMonthStartGap = new Set<number>(); // value is rowStart, for rows that cross through a start of month gap but does not start the selection
  const rowsCrossingMonthEndGap = new Set<number>(); // value is rowStart, for rows that cross through a end of month gap but does not end the selection
  const daysForRows = new Map<number, CalendarDay[]>(); // key is rowStart, value is array of days associated with the row

  const selectionDate = (() => {
    const {year, month, isoDayOfMonth: day} = monthGridLayout.days.get(monthGridLayout.startDayKey).calendarDay;
    const gridStartDate = moment({year, month, day});
    return gridStartDate.isAfter(timespanStart) ? gridStartDate : timespanStart.clone();
  })();
  const segmentEndDate = (() => {
    const {year, month, isoDayOfMonth: day} = monthGridLayout.days.get(monthGridLayout.endDayKey).calendarDay;
    const gridEndDate = moment({year, month, day});
    return gridEndDate.isBefore(timespanStart) ? gridEndDate : timespanEnd.clone();
  })();
  while (selectionDate.isSameOrBefore(segmentEndDate, `day`)) {
    const dayKey = getCalendarDayKey({
      year: selectionDate.year(),
      month: selectionDate.month(),
      isoDayOfMonth: selectionDate.date(),
    });
    if (monthGridLayout.days.has(dayKey)) {
      const day = monthGridLayout.days.get(dayKey);

      // track column boundaries
      if (!minColumnForRows.has(day.rowStart) || day.columnStart < minColumnForRows.get(day.rowStart)) {
        minColumnForRows.set(day.rowStart, day.columnStart);
      }
      if (!maxColumnForRows.has(day.rowStart) || day.columnStart > maxColumnForRows.get(day.rowStart)) {
        maxColumnForRows.set(day.rowStart, day.columnStart);
      }

      // track rows that cross through start/end of month gaps
      if (day.calendarDay.isoDayOfMonth === 1 && day.columnStart > 1 && selectionDate.isAfter(rangeStart, `day`)) {
        minColumnForRows.set(day.rowStart, 1); // extend segment to far left
        rowsCrossingMonthStartGap.add(day.rowStart);
      }
      if (
        day.calendarDay.isoDayOfMonth === selectionDate.daysInMonth() &&
        day.columnStart < 7 &&
        selectionDate.isBefore(rangeEnd, `day`)
      ) {
        maxColumnForRows.set(day.rowStart, 7); // extend segment to far right
        rowsCrossingMonthEndGap.add(day.rowStart);
      }

      if (!daysForRows.has(day.rowStart)) {
        daysForRows.set(day.rowStart, []);
      }
      daysForRows.get(day.rowStart).push(day.calendarDay);
    }
    selectionDate.add(1, `day`);
  }

  return Array.from(minColumnForRows.keys()).map((rowStart: number): SelectionSegmentLayout => {
    const minColumnStart = minColumnForRows.get(rowStart);
    const maxColumnStart = maxColumnForRows.get(rowStart);
    return {
      segmentType,
      verticalMarginPx: segmentType === SelectionSegmentType.Highlight ? 0 : 1,
      columnStart: minColumnStart,
      columnSpan: maxColumnStart - minColumnStart + 1,
      rowStart,
      startRounded: rowStart === rangeStartRowStart && rangeStart.isSame(timespanStart),
      endRounded: rowStart === rangeEndRowStart && rangeEnd.isSame(timespanEnd),
      fadeIn: rowsCrossingMonthStartGap.has(rowStart),
      fadeOut: rowsCrossingMonthEndGap.has(rowStart),
      calendarDays: daysForRows.get(rowStart),
    };
  });
}

// TODO opslead
export function generateSelectionLayout(
  selection: ScrollingCalendarDateSelection,
  monthGridLayout: MonthGridLayout,
): SelectionLayout {
  const fillStart = moment(selection.start, moment.ISO_8601);
  const fillEnd = moment(selection.end, moment.ISO_8601);
  if (!(fillStart.isValid() && fillEnd.isValid())) {
    throw new Error(`@selection.start and/or @selection.end must be valid ISO 8601 date strings`);
  }
  if (fillStart.isAfter(fillEnd, `day`)) {
    throw new Error(`@selection.start's date cannot be after @selection.end`);
  }

  // range describes the total visual range, including "offsetFrom" if it's defined
  let rangeStart: moment.Moment = fillStart.clone();
  let rangeEnd: moment.Moment = fillEnd.clone();

  if (selection.offsetFrom) {
    const offsetDate = moment(selection.offsetFrom, moment.ISO_8601);
    if (offsetDate.isValid()) {
      if (offsetDate.isBefore(fillStart, `day`)) {
        rangeStart = offsetDate;
      } else if (offsetDate.isAfter(fillEnd, `day`)) {
        rangeEnd = offsetDate;
      } else {
        throw new Error(
          `"selection" JSON attribute's "offsetFrom" property must describe a date before "start" or after "end"`,
        );
      }
    } else {
      throw new Error(
        `"selection" JSON attribute's "offsetFrom" property must be a valid ISO 8601 date string where the date is before "start" or after "end"`,
      );
    }
  }

  const fillSegments = generateSegmentsForSegmentType(
    monthGridLayout,
    fillStart,
    fillEnd,
    rangeStart,
    rangeEnd,
    SelectionSegmentType.Fill,
  );

  const offsetSegments: SelectionSegmentLayout[] = [];
  if (rangeStart.isBefore(fillStart, `day`)) {
    const leadingOffsetSegments = generateSegmentsForSegmentType(
      monthGridLayout,
      rangeStart,
      fillStart, // offset segments will overlap under the fill segments by one day to achieve the right visual appearance
      rangeStart,
      rangeEnd,
      SelectionSegmentType.Offset,
    );
    offsetSegments.push(...leadingOffsetSegments);
  }
  if (rangeEnd.isAfter(fillEnd, `day`)) {
    const trailingOffsetSegments = generateSegmentsForSegmentType(
      monthGridLayout,
      fillEnd, // offset segments will overlap under the fill segments by one day to achieve the right visual appearance
      rangeEnd,
      rangeStart,
      rangeEnd,
      SelectionSegmentType.Offset,
    );
    offsetSegments.push(...trailingOffsetSegments);
  }

  const highlightSegments: SelectionSegmentLayout[] = [];
  if (selection.highlightStart) {
    const [segment] = generateSegmentsForSegmentType(
      monthGridLayout,
      fillStart,
      fillStart,
      fillStart,
      fillStart,
      SelectionSegmentType.Highlight,
    );
    if (segment) {
      highlightSegments.push(segment);
    }
  }
  if (selection.highlightEnd) {
    const [segment] = generateSegmentsForSegmentType(
      monthGridLayout,
      fillEnd,
      fillEnd,
      fillEnd,
      fillEnd,
      SelectionSegmentType.Highlight,
    );
    if (segment) {
      highlightSegments.push(segment);
    }
  }

  // array ordering matters when segments overlap, segments will overlap each other in order
  const segments = [...offsetSegments, ...fillSegments, ...highlightSegments];

  const activeSegmentForDays = new Map<string, SelectionSegmentLayout>();
  for (const segment of segments) {
    for (const calendarDay of segment.calendarDays) {
      activeSegmentForDays.set(getCalendarDayKey(calendarDay), segment);
    }
  }

  return {segments, activeSegmentForDays};
}
